import { Component, OnInit, OnDestroy, Input, ElementRef, AfterViewInit, ViewChild, Output, EventEmitter } from '@angular/core';

import { Modal, BSModalContext } from 'ngx-modialog-11/plugins/bootstrap';
import { overlayConfigFactory } from 'ngx-modialog-11';

import { MessageBoxComponent } from '../../dialogs/message-box/message-box.component';
import { PdfCreateService } from '../../services/pdf/pdf-create.service';

import * as SignaturePad from 'signature_pad';
import { Subscription } from 'rxjs';
import { AppearanceService } from '@app/core/dialogs/appearance/appearance.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-draw-canvas',
  templateUrl: './draw-canvas.component.html',
  styleUrls: ['./draw-canvas.component.scss'],
})
export class DrawCanvasComponent implements OnInit, AfterViewInit, OnDestroy {
  public signaturePad;
  public drawing = false;

  private subscription: Subscription;

  @ViewChild('canvas') canvas: ElementRef;

  @Input() description;
  @Input() model;

  @Input() width = 800;
  @Input() height = 180;
  @Input() public color = '#444444';

  @Output()
  onDraw = new EventEmitter();

  @Output()
  onClear = new EventEmitter();

  private context: CanvasRenderingContext2D;
  private isUseDefault = false;

  constructor(
    private pdfCreateService: PdfCreateService,
    private modal: Modal,
    private appearanceService: AppearanceService,
  ) {}

  ngOnInit() {
    this.subscription = this.appearanceService.currentDrawColor().subscribe((color: string) => {
      this.color = color;

      if (this.signaturePad) {
        this.signaturePad.penColor = color;
      }
    });
  }

  ngAfterViewInit() {
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;

    this.context = canvasEl.getContext('2d');

    canvasEl.width = this.width;
    canvasEl.height = this.height;

    if (!SignaturePad) {
      return;
    }
    const data = this.isUseDefault
      ? {}
      : {
          minWidth: 5,
          maxWidth: 5,
          penColor: this.color,
        };

    this.signaturePad = new SignaturePad.default(canvasEl, data);

    this.signaturePad.onEnd = () => {
      this.uploadFile(this.signaturePad.toDataURL(), this.signaturePad.toData());
    };

    if (this.model && this.model.drawData) {
      this.signaturePad.fromData(this.model.drawData);
    }
  }

  isEmpty() {
    return this.signaturePad ? this.signaturePad.isEmpty() : true;
  }

  clear() {
    this.signaturePad.clear();
    this.model.data = null;
    this.model.drawData = null;

    this.onClear.emit({
      data: this.model.data,
    });
  }

  ngOnDestroy() {
    if (this.signaturePad) {
      this.signaturePad.off();
    }

    this.subscription.unsubscribe();
  }

  private uploadFile(dataURL: any, data: any) {
    const blob = this.dataURItoBlob(dataURL);
    const file = this.blobToFile(blob, 'image.png', 'image/png');

    this.model.pending = true;
    this.pdfCreateService
      .createPdf(file)
      .pipe(
        finalize(() => {
          this.model.pending = false;
        }),
      )
      .subscribe(
        (resonse) => {
          this.model.drawData = data;
          this.model.data = dataURL;
          this.onResponse(resonse);
        },
        (error) => {
          this.onError(error);
        },
      );
  }

  private onError(response?) {
    this.clear();
    this.modal.open(
      MessageBoxComponent,
      overlayConfigFactory(
        {
          description: 'Could not upload image to server',
          classes: 'notitification-popup error-popup',
          buttons: [
            {
              text: 'Ok',
            },
          ],
          onClose: () => {},
        },
        BSModalContext,
      ),
    );
  }

  private onResponse(fileId) {
    if (!fileId) {
      this.onError();
    }
    const image = new Image();

    image.onload = () => {
      this.model.image = image;

      this.onDraw.emit({
        data: this.model.data,
        fileId: fileId,
      });
    };
    image.src = this.model.data;
  }

  private dataURItoBlob(dataURI) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    var byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);

    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }

  private blobToFile(blob, fileName, contentType) {
    blob.lastModifiedDate = new Date();
    blob.name = fileName;

    return blob;
  }
}
